import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureInteger } from "../../common/utils/generalUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { dateRangeOverlap} from "../../common/utils/dateUtils";
import { logout } from "../../common/reducers/initContext";
import { isActive } from "./rosterMgrSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { aggregatingFetch } from "../../common/hooks/networkHook";

const key = roster => ensureInteger(roster.rosterid);

const updateState = (state, roster) => {
    state.data[key(roster)] = roster;
}

// NB: This is it's own slice as unconfirmed rosters have a different set of permissions to regular rosters.
// For example (assuming disableMemberHoursConfirmation is false), you can only view unconfirmed rosters
// that either belong to you, or for shifts/events (depening on isolation mode) you were a commander on.
// If disableMemberHoursConfirmation is true, you cannot view any unonfirmed rosters

export const rosterUnconfirmedStaffSlice = createSlice({
    name: 'staffUnconfirmedRosters',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            ensureArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toProcess.delete(key(roster));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(r => {
                if (r.hours_confirmed) {
                    delete state.data[key(r)];
                } else {
                    state.data[key(r)] = r
                }
            });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    if (roster.shiftid === shift.id) {
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                        roster.shift_travel = shift.travel;
                    }
                }
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => delete state.data[key(roster)])
        },
        extraReducers: builder => {
            builder.addCase(logout, () => rosterUnconfirmedStaffSlice.getInitialState());
        }
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'roster_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'roster_one');

const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/staffRoster/' + encodeURIComponent(id), data => rosterUnconfirmedStaffSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffRosters', ids, data => rosterUnconfirmedStaffSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

export const fetchUnconfirmedRostersInRange = (start, end, config) => network => network.post('/api/v2/staffRosterUnconfirmed', {start, end}, rosterUnconfirmedStaffSlice.actions.addOrUpdate, visualizeFailure('get'))

// #endregion
const selectRawRosters = state => state.staffUnconfirmedRosters.data;

const selectUnconfirmedRosters = (start, end, config) => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => !r.hours_confirmed && isActive(r, config) && dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? []);
// #region selectors

// #endregion

const listen = {
    rosterStaffCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'roster', roster);
        dispatch(rosterUnconfirmedStaffSlice.actions.addOrUpdate(roster))
    },
    rosterStaffUpdated: (roster, { dispatch }) => dispatch(rosterUnconfirmedStaffSlice.actions.addOrUpdate(roster)),
    rosterStaffRemoved: (roster, { dispatch }) => dispatch(rosterUnconfirmedStaffSlice.actions.remove(roster)),
    shiftStaffUpdated: (shift, { dispatch }) => dispatch(rosterUnconfirmedStaffSlice.actions.updateShift(shift))
}

const options = {
    cleanup: { root: rosterUnconfirmedStaffSlice.name, all: state => Object.values(selectRawRosters(state)), removeAll: rosterUnconfirmedStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member')
}
const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'staffUnconfirmedRosterByID' });
registerEntityBasedFetch('roster', rosterInitByID);

export const [useStaffUnconfirmedRostersInRange] = dataHookWithCredentialsFactory(selectUnconfirmedRosters, queryMapFactory(fetchUnconfirmedRostersInRange, 'rosterid', rosterInitByID, listen), options);

const reducer = { name: rosterUnconfirmedStaffSlice.name, reducer: rosterUnconfirmedStaffSlice.reducer }
export default reducer;